import filter from 'lodash/filter'
import { LOCATION_CHANGE } from 'redux-first-history'

import {
  ADDRESS_INPUT_FIELD_UPDATE,
  ADD_ADDRESS_FORM_VALIDATION_ERROR,
  LOAD_GLOBAL_DATA
} from 'constants/action_types'
import * as type from 'constants/action_types/accounts'
import * as apiKeysTypes from 'constants/action_types/api_keys'
import { MWS_SYNC_STATUS, PPC_SYNC_STATUS } from 'constants/account'

import { updateInArray, updateAllInArray, replaceAtIndex } from 'helpers/arrays'
import { getShowState } from 'helpers/core'

export const initialState = {
  userInfo: {
    firstName: '',
    lastName: '',
    email: '',
    oldPassword: '',
    newPassword: '',
    users: ''
  },

  profileValidations: {
    firstName: '',
    lastName: '',
    email: '',
    newPassword: '',
    users: ''
  },

  authy: {
    hasAuthy: false,
    code: '',
    phoneNumber: '',
    isLoading: false,
    completed: false
  },

  billing: {
    settings: {},
    invoices: [],
    nextInvoice: null,
    periodEnd: null,
    checkAll: null,
    numberChecked: null,
    activeInvoice: null,
    page: 1
  },

  stripeSubscriptionInfo: {
    onTrial: false,
    trialEndDate: null,
    samePeriodBundle: false
  },

  billingAddress: {
    firstName: '',
    lastName: '',
    address_line1: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: ''
  },

  billingAddressValidations: {
    firstName: '',
    lastName: '',
    address_line1: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: ''
  },

  churnFunnel: {
    answers: {
      reason: null
    }
  },

  showAddUserModal: false,
  showAuthyModal: false,
  showUpgradeModal: false,

  page: {
    isLoading: false,
    completed: false
  },

  stripe: {
    isLoading: false,
    completed: false
  },

  saveAccount: {
    isLoading: false,
    completed: false
  },

  saveProfile: {
    isLoading: false,
    completed: false
  },

  savePassword: {
    isLoading: false,
    completed: false
  },

  cancel: {
    isLoading: false,
    completed: false
  },

  pause: {
    isLoading: false,
    completed: false
  },

  reactivate: {
    isLoading: false,
    completed: false
  },

  invoices: {
    isLoading: false,
    completed: false
  },

  viewInvoice: {
    isLoading: false,
    completed: false
  },

  downloadInvoice: {
    isLoading: false,
    completed: false
  },

  emailInvoice: {
    isLoading: false,
    completed: false
  },

  uploadingPhoto: {
    isLoading: false,
    completed: false
  },

  deletingPhoto: {
    isLoading: false,
    completed: false
  },

  inviteUsers: {
    isLoading: false,
    completed: false,
    email: null
  },

  revokeUser: {
    isLoading: false,
    completed: false,
    email: null
  },

  creditCard: {
    isLoading: false,
    completed: false
  },

  unbindAlipayLoading: false,
  bindAlipayLoading: false,

  mwsSync: {
    isLoading: false,
    isStatusLoading: false,
    availableMarketplaces: {
      eu: [],
      na: [],
      fe: []
    },
    status: {
      overall_percentage: undefined,
      amazon_seller_accounts: []
      /* structure for ^ {
        "id": 8,
        "mws_sync": 0,
        "mws_product": 100,
        "mws_order": 0,
        "mws_finance": 0.410396716826265
      } */
    }
  },

  ui: {
    amazonSellerAccounts: {
      editingAccount: null, // the account ID for the account we're editing
      modalOpen: null, // one of AMAZON_SELLER_ACCOUNT_MODALS or null if none open
      ppcSyncStatus: PPC_SYNC_STATUS.initial
    },
    mwsSync: {
      mwsSyncStatus: MWS_SYNC_STATUS.initial,
      ppcSyncStatus: PPC_SYNC_STATUS.initial
    }
  },

  apiKeys: {
    keys: [],
    createdKey: {
      name: '',
      raw_key: ''
    },
    requestCount: 0,
    billingPeriodStart: '',
    limit: 0,
    isLoading: false,
    isCreating: false,
    completed: false,
    isRevoking: false,
    generateModalOpen: false
  }
}

const addUserInfo = (state, { payload }) => {
  if (!payload.user) return state

  const {
    first_name: firstName,
    last_name: lastName,
    email,
    is_amazon_seller: isAmazonSeller
  } = payload.user

  return {
    ...state,
    userInfo: {
      ...state.userInfo,
      firstName,
      lastName,
      email,
      isAmazonSeller
    }
  }
}

const closePpcModal = state => {
  return {
    ...state,
    ui: {
      ...state.ui,
      amazonSellerAccounts: {
        ...state.ui.amazonSellerAccounts,
        editingAccount: null,
        modalOpen: null
      }
    }
  }
}

const closeMwsModal = state => {
  return {
    ...state,
    mwsSync: {
      ...state.mwsSync,
      data: initialState.mwsSync.data
    },
    ui: {
      ...state.ui,
      mwsSync: initialState.ui.mwsSync
    }
  }
}

function account(state = initialState, action) {
  switch (action.type) {
    case type.PENDING_ACCOUNT_ACTION:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          isLoading: true
        }
      }
    case type.COMPLETED_ACCOUNT_ACTION:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          isLoading: false,
          completed: true
        }
      }
    case type.PENDING_CREATING_ACCOUNT_ACTION:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          isCreating: true
        }
      }
    case type.COMPLETED_CREATING_ACCOUNT_ACTION:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          isCreating: false
        }
      }
    case type.RESET_ACCOUNT_ACTION:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          isLoading: false,
          completed: false
        }
      }
    case type.COMPLETED_LOAD_ACCOUNT:
      return {
        ...state,
        ...action.payload,
        page: {
          isLoading: false,
          completed: true
        }
      }
    case type.COMPLETED_BILLING_ACTION:
      return {
        ...state,
        billing: {
          ...state.billing,
          ...action.payload
        }
      }
    case type.ACCOUNT_INPUT_FIELD_UPDATE:
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          [action.payload.prop]: action.payload.value
        }
      }
    case type.ACCOUNT_DROPDOWN_FIELD_UPDATE:
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          [action.payload.prop]: action.payload.value
        }
      }
    case type.ADD_ACCOUNT_PROFILE_VALIDATION_ERROR:
      const { field, message } = action.payload
      return {
        ...state,
        profileValidations: {
          ...state.profileValidations,
          [field]: message
        }
      }
    case ADDRESS_INPUT_FIELD_UPDATE:
      return {
        ...state,
        billingAddress: {
          ...state.billingAddress,
          [action.payload.prop]: action.payload.value
        }
      }
    case ADD_ADDRESS_FORM_VALIDATION_ERROR: {
      const { field, message } = action.payload
      return {
        ...state,
        billingAddressValidations: {
          ...state.billingAddressValidations,
          [field]: message
        }
      }
    }
    case type.RESET_USER_INFO:
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          oldPassword: initialState.userInfo.oldPassword,
          newPassword: initialState.userInfo.newPassword
        }
      }
    case type.TOGGLE_AUTHY_SIGNUP_MODAL:
      return {
        ...state,
        showAuthyModal: getShowState(state.showAuthyModal, action.payload),
        authy: {
          ...state.authy,
          phoneNumber: ''
        }
      }
    case type.AUTHY_INPUT_FIELD_UPDATE:
      return {
        ...state,
        authy: {
          ...state.authy,
          [action.payload.prop]: action.payload.value
        }
      }
    case type.TOGGLE_CHECK_INVOICE: {
      const newCheckedState = getShowState(action.payload.checked)

      const updatedInvoices = updateInArray(
        state.billing.invoices,
        { id: action.payload.id },
        { checked: newCheckedState }
      )

      const updatedCount = filter(updatedInvoices, {
        checked: true
      }).length

      return {
        ...state,
        billing: {
          ...state.billing,
          invoices: updatedInvoices,
          checkAll: updatedCount === state.billing.invoices.length,
          numberChecked: updatedCount
        }
      }
    }
    case type.TOGGLE_CHECK_ALL_INVOICES: {
      const newCheckedState = getShowState(state.billing.checkAll)

      const updatedInvoices = updateAllInArray(state.billing.invoices, {
        checked: newCheckedState
      })

      const updatedCount = filter(updatedInvoices, {
        checked: true
      }).length

      return {
        ...state,
        billing: {
          ...state.billing,
          invoices: updatedInvoices,
          checkAll: newCheckedState,
          numberChecked: updatedCount
        }
      }
    }
    case type.RESET_CHECKED_INVOICES: {
      const updatedInvoices = updateAllInArray(state.billing.invoices, {
        checked: false
      })

      return {
        ...state,
        billing: {
          ...state.billing,
          invoices: updatedInvoices,
          checkAll: false,
          numberChecked: 0
        }
      }
    }
    case type.INVOICE_INPUT_FIELD_UPDATE:
      return {
        ...state,
        billing: {
          ...state.billing,
          settings: {
            ...state.billing.settings,
            touched: true,
            [action.payload.prop]: action.payload.value
          }
        }
      }
    case type.SET_ACTIVE_INVOICE:
      return {
        ...state,
        billing: {
          ...state.billing,
          activeInvoice: action.payload
        }
      }
    case type.TOGGLE_REVOKE_USER_CONFIRM_MODAL: {
      return {
        ...state,
        showRevokeUserConfirmModal: getShowState(
          state.showRevokeUserConfirmModal
        ),
        revokeUser: {
          ...state.revokeUser,
          email: action.payload
        }
      }
    }
    case type.ACCOUNT_AMAZON_SELLER_ACCOUNT_SHOW_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          amazonSellerAccounts: {
            ...state.ui.amazonSellerAccounts,
            editingAccount: action.payload.accountId,
            modalOpen: action.payload.modal,
            ppcSyncStatus:
              action.payload.ppcSyncStatus || PPC_SYNC_STATUS.initial
          }
        }
      }
    case type.ACCOUNT_AMAZON_SELLER_ACCOUNT_CLOSE_MODAL:
      return closePpcModal(state)
    case type.ACCOUNT_AMAZON_SELLER_ACCOUNT_GET_STATUS_PENDING_STATUS:
      return {
        ...state,
        mwsSync: {
          ...state.mwsSync,
          isStatusLoading: true
        }
      }
    case type.ACCOUNT_AMAZON_SELLER_ACCOUNT_GET_STATUS_COMPLETE:
      return {
        ...state,
        mwsSync: {
          ...state.mwsSync,
          status: action.payload
        }
      }
    case type.ACCOUNT_AMAZON_SELLER_ACCOUNT_GET_STATUS_COMPLETE_STATUS:
      return {
        ...state,
        mwsSync: {
          ...state.mwsSync,
          isStatusLoading: false
        },
        ui: {
          ...state.ui,
          mwsSync: {
            ...state.ui.mwsSync
          }
        }
      }
    case LOAD_GLOBAL_DATA: {
      const newState = addUserInfo(state, action)
      return newState
    }
    case type.ACCOUNT_MARKETPLACE_LIST_LOAD_COMPLETE:
      return {
        ...state,
        mwsSync: {
          ...state.mwsSync,
          availableMarketplaces: {
            ...state.mwsSync.availableMarketplaces,
            na: action.payload.primary_marketplaces.NA.map(item => ({
              countryCode: item[0],
              name: item[2],
              value: item[1]
            })),
            eu: action.payload.primary_marketplaces.EU.map(item => ({
              countryCode: item[0],
              name: item[2],
              value: item[1]
            })),
            fe: action.payload.primary_marketplaces.FE.map(item => ({
              countryCode: item[0],
              name: item[2],
              value: item[1]
            }))
          }
        }
      }
    case LOCATION_CHANGE:
      if (state?.ui?.mwsSync?.mwsSyncStatus !== 'initial') {
        return state
      }
      const cleanedState = [closeMwsModal, closePpcModal].reduce(
        (state, func) => func(state),
        state
      )
      return cleanedState

    case type.UNBIND_ALIPAY_LOADING:
      return { ...state, unbindAlipayLoading: action.payload }

    case type.BIND_ALIPAY_LOADING:
      return { ...state, bindAlipayLoading: action.payload }

    case apiKeysTypes.SET_API_KEYS:
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          keys: action.payload.apiKeys,
          requestCount: action.payload.apiRequests.total_count,
          billingPeriodStart: action.payload.apiRequests.start_date,
          limit: action.payload.apiRequests.limit
        }
      }
    case apiKeysTypes.SET_GENERATE_KEY_MODAL_OPEN:
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          createdKey: {
            raw_key: '',
            name: ''
          },
          generateModalOpen: action.payload
        }
      }
    case apiKeysTypes.SET_CREATED_KEY:
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          createdKey: {
            raw_key: action.payload.raw_key,
            name: action.payload.name
          },
          generateModalOpen: false
        }
      }
    case apiKeysTypes.ADD_CREATED_KEY:
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          keys: [
            ...state.apiKeys.keys,
            {
              active: action.payload.active,
              application_name: action.payload.application_name,
              created_at: action.payload.created_at,
              first_four: action.payload.first_four,
              id: action.payload.id,
              key: action.payload.key,
              name: action.payload.name
            }
          ]
        }
      }
    case apiKeysTypes.PENDING_REVOKE_API_KEY:
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          isRevoking: true
        }
      }
    case apiKeysTypes.COMPLETED_REVOKE_API_KEY:
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          isRevoking: false
        }
      }
    case apiKeysTypes.SUCCESSFUL_REVOKE_API_KEY: {
      const index = state.apiKeys.keys.findIndex(
        key => key.id === action.payload.id
      )
      const updatedKey = { ...state.apiKeys.keys[index], active: false }

      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          keys: replaceAtIndex(state.apiKeys.keys, index, updatedKey)
        }
      }
    }
    case apiKeysTypes.RESET_API_KEY:
      return {
        ...state,
        apiKeys: {
          ...initialState.apiKeys
        }
      }
    default:
      return state
  }
}

export default account
